import type { ReactNode } from "react";
import type { Recap } from "../../lib/api";
import { LinkButton } from "../ui/button";
import { RecapItemCard } from "./recap-item";

/** The default greeting when the server sends none. No exclamation — Nob does not shout. */
export const DEFAULT_GREETING = "Morning, big nob.";

/** What a quiet morning is called. Silence is a feature (FR-7.5), not an error. */
export const SUPPRESSED_TITLE = "Nothing worth waking you for.";

/**
 * A DOOR THAT OPENED IS THE HEADLINE (ruling R-0910-7 rule 1). The owner: "a
 * door that opened is one of the most important things that are worth waking
 * you for" — so it displaces the greeting AND the quiet morning, which are
 * both statements about a door this person owns, while this is the one thing
 * on the briefing that happened TO them.
 */
export function doorsOpenedTitle(n: number): string {
  return `${n} ${n === 1 ? "door" : "doors"} opened for you.`;
}

export const MINIMISE_LABEL = "Minimise the briefing";
export const REOPEN_LABEL = "Open the briefing";

/**
 * The briefing's title — a door that opened for this person, the server's
 * greeting, or the quiet morning's name, in that order. The counts ride under
 * it unchanged in every one of the three: the door is news, not a replacement
 * for the morning's numbers.
 */
export function recapTitle(recap: Recap): string {
  if (recap.doors_opened > 0) return doorsOpenedTitle(recap.doors_opened);
  return recap.suppressed ? SUPPRESSED_TITLE : (recap.greeting ?? DEFAULT_GREETING);
}

/**
 * The two counts, in one line, in one string.
 *
 * ONE STRING BECAUSE THE MINIMISED BAR IS ONE LINE (owner, 2026-08-26). The
 * subtitle used to be a paragraph that collapsed to nothing the moment the log
 * moved, so the fact an owner wanted most — how much was kept off their plate —
 * was the first thing to go. It rides beside the title everywhere now, at every
 * size, which only works if it is a sentence rather than a layout.
 */
export function recapCounts(recap: Recap): string {
  if (recap.suppressed) {
    return `${recap.total_knocks} ${recap.total_knocks === 1 ? "knock" : "knocks"}, none cleared the bar.`;
  }
  return `${recap.surfaced_count} worth a look · ${recap.filtered_count} kept off your plate`;
}

type Props = {
  recap: Recap;
  /** The grounded insights section (FR-7.4), built by the container. */
  insights: ReactNode;
  onSeeAll: () => void;
  onMinimise: () => void;
  /** Open one knock's thread in the console — the card's only affordance. */
  onOpenKnock: (knockId: string) => void;
};

/**
 * The Morning Nob (M16, FR-7.3) — AN OVERLAY OVER THE CONVERSATION, since the
 * owner's ruling of 2026-08-26.
 *
 * It was the top of the chat log: the briefing scrolled, so reading one message
 * pushed it away, and the title had to be extracted into a masthead that shrank
 * to survive. Two halves of one thing on opposite sides of a scroller, each with
 * its own state machine. It is one object again — a card laid OVER the log,
 * open when the console opens, minimised to a bar when the owner wants the
 * conversation. Nothing about it is modal: there is no scrim, nothing is
 * trapped, and the composer under it still takes a sentence.
 *
 * A pure render of what it is handed; every piece of network state lives in
 * `RecapArea`, which is what lets the whole briefing be asserted directly.
 */
export function RecapOverlayCard({ recap, insights, onSeeAll, onMinimise, onOpenKnock }: Props) {
  return (
    // `py-3` is not symmetry for its own sake: nob mode hangs a hard ink shadow
    // 5px under this card, and the layer is what would clip it.
    <div className="pointer-events-none absolute inset-0 z-20 flex justify-center px-3 py-3 sm:px-4">
      <section
        aria-label="The Morning Nob"
        // What "outside the card" means, for the dismiss in `RecapArea`. An
        // attribute rather than the style class, because the class is a look and
        // this is a boundary — a second card wearing the same skin somewhere
        // else must not silently become part of this one.
        data-recap-card
        // `h-fit` IS THE WHOLE CARD (2026-09-13). The layer is `inset-0`, so a
        // card left to the default `stretch` was a 696px box over the thread
        // with a briefing's worth of type at the top of it and dead white
        // underneath — and its scroller, being `pointer-events-auto`, took
        // every press aimed at the log. The card is its content's height and
        // nothing more; `max-h-full` is the ceiling that turns the body into a
        // scroller only when a long morning needs one.
        className="nob-recap-card nob-enter pointer-events-auto flex h-fit max-h-full w-full
                   max-w-2xl flex-col overflow-hidden rounded-2xl"
      >
        <header className="flex flex-none items-start gap-4 px-6 pt-5 pb-4">
          <div className="min-w-0 flex-1">
            {/* THE HEADLINE IS THE WAY IN when a door has opened (ruling
                R-0910-7 rule 1): the news and the room are one press apart, and
                the room is the newest of them. Everywhere else it is a
                heading, because there is nothing behind it to open. */}
            <h1 className="text-balance text-[26px] font-semibold leading-tight tracking-tight text-text sm:text-[30px]">
              {recap.doors_opened > 0 && recap.doors_opened_knock_id ? (
                <button
                  type="button"
                  onClick={() => onOpenKnock(recap.doors_opened_knock_id as string)}
                  className="nob-press text-left underline decoration-accent/40 underline-offset-4
                             transition-colors duration-75 hover:decoration-accent
                             focus-visible:outline-none focus-visible:ring-2
                             focus-visible:ring-focus/70"
                >
                  {recapTitle(recap)}
                </button>
              ) : (
                recapTitle(recap)
              )}
            </h1>
            {/* THE COUNTS SAY WHAT THEY ARE COUNTS OF (owner, 2026-08-27). This
                overlay covers the last day; the emailed digest covers whatever
                has arrived since the last one. Two windows, so neither number
                means anything until the card names the one it came from. */}
            <p className="mt-2 text-[15px] leading-relaxed text-muted">
              {recap.window_label && (
                <span className="font-medium text-text-soft">{recap.window_label} · </span>
              )}
              {recapCounts(recap)}
            </p>
          </div>
          <button
            type="button"
            onClick={onMinimise}
            aria-label={MINIMISE_LABEL}
            className="nob-press -mr-1 grid size-8 shrink-0 place-items-center rounded-full text-faint
                       transition-colors duration-75 hover:bg-hover hover:text-text
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
          >
            <MinimiseIcon />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-6">
          {recap.suppressed ? null : (
            <>
              {recap.time_saved_label && (
                <p className="text-[15px] leading-relaxed text-accent-ink-strong/90">
                  Your Nob saved you {recap.time_saved_label} of inbox archaeology.
                </p>
              )}

              {recap.items.length > 0 && (
                <ul className="mt-5 space-y-4">
                  {recap.items.map((item) => (
                    <RecapItemCard
                      key={item.knock_id}
                      item={item}
                      onOpen={() => onOpenKnock(item.knock_id)}
                    />
                  ))}
                </ul>
              )}

              {insights}

              {recap.filtered_count > 0 && (
                <div className="mt-10 flex items-center justify-between gap-4 border-t border-border-soft pt-6">
                  <p className="text-[14px] leading-relaxed text-faint">
                    {recap.filtered_count} others didn&rsquo;t make the cut.
                  </p>
                  <LinkButton type="button" onClick={onSeeAll}>
                    All passed knocks &rarr;
                  </LinkButton>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

/**
 * THE BRIEFING, FOLDED TO ONE LINE — and it keeps its subtitle (owner,
 * 2026-08-26).
 *
 * The masthead this replaces dropped the counts the moment the log moved, on
 * the argument that a scrolling reader wants the thread. True, and the counts
 * were the wrong thing to spend on it: "41 kept off your plate" IS the
 * briefing, and a title alone says only that a briefing exists. Both facts, one
 * line, always — and the whole bar is the way back to the card.
 *
 * AND IT IS A CONTROL, NOT A CAPTION (owner, 2026-08-27). Folded, it was two
 * runs of grey type on the shelf's own ground with a hover tint for company:
 * everything about it said "heading", nothing said "press me", and an owner who
 * minimised the card had no visible way back. It wears the accent now, at the
 * full width of the card it folds — with the chevron that names the direction
 * the card comes back from, a raise under the cursor, and the 44px a thumb
 * needs. Nob mode takes the same bargain `.nob-prompt-cta` strikes one shelf
 * over: an accent fill on paper is a yellow lozenge against yellow, so the skin
 * draws the edge in ink.
 */
export function RecapBar({ recap, onOpen }: { recap: Recap; onOpen: () => void }) {
  return (
    // `px-3 sm:px-4` is the CARD's outer padding, not a guess: folded and open
    // are one object, and an object that changes width when it folds reads as
    // two.
    <div className="mx-auto w-full max-w-2xl px-3 sm:px-4">
      <button
        type="button"
        onClick={onOpen}
        aria-label={REOPEN_LABEL}
        className="nob-recap-bar flex w-full items-center gap-3 rounded-2xl px-4 py-2 text-left
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
      >
        <span className="min-w-0 flex-1 truncate">
          <span className="text-[15px] font-semibold tracking-tight">{recapTitle(recap)}</span>{" "}
          <span className="nob-recap-bar-counts text-[13px] font-semibold">
            {recapCounts(recap)}
          </span>
        </span>
        <ReopenIcon />
      </button>
    </div>
  );
}

/**
 * The way back, pointing the way the card returns: the minimise folds it upward
 * into this bar, so the affordance that unfolds it points down. The stroke is
 * heavier than the minimise's because it sits ON the accent rather than beside
 * the type, where a hairline disappears.
 */
function ReopenIcon() {
  return (
    <svg viewBox="0 0 20 20" className="size-5 shrink-0" fill="none" aria-hidden="true">
      <path
        d="M5 8 10 13l5-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MinimiseIcon() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
      <path
        d="M5 12.5 10 7.5l5 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
